import ambulanceIcon from '../svg/Rectangle.png';
import locationPinIcon from '../svg/pickup.png';
import hospitalPinIcon from '../svg/hospital.png';

/**
 * Driver / Ambulance marker — 60×60 PNG, centered anchor, rotatable.
 */
export const getDriverMarker = (rotation = 0) => {
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps not available');
  }

  return {
    url: ambulanceIcon,
    scaledSize: new window.google.maps.Size(60, 60),
    anchor: new window.google.maps.Point(30, 30),
    rotation,
  };
};

/**
 * Generic location pin marker (blue pin) — used for patient/home location.
 */
export const getLocationPinMarker = () => {
  // Always return a usable icon; when Maps is available we add size/anchor.
  if (!window.google || !window.google.maps) {
    return { url: locationPinIcon };
  }

  return {
    url: locationPinIcon,
    scaledSize: new window.google.maps.Size(48, 48),
    anchor: new window.google.maps.Point(24, 48),
  };
};

/**
 * Hospital marker (hospital building icon) — used for hospital location.
 */
export const getHospitalMarker = () => {
  // Always return a usable icon; when Maps is available we add size/anchor.
  if (!window.google || !window.google.maps) {
    return { url: hospitalPinIcon };
  }

  return {
    url: hospitalPinIcon,
    scaledSize: new window.google.maps.Size(50, 50),
    anchor: new window.google.maps.Point(25, 50),
  };
};

/**
 * Returns the correct marker icons for pickup and dropoff based on journey direction.
 * OUTBOUND (default): pickup = location pin (home), dropoff = hospital
 * INBOUND: pickup = hospital, dropoff = location pin (home)
 */
export const getDirectionalMarkers = (journeyDirection) => {
  const isInbound = journeyDirection?.toUpperCase() === 'INBOUND';

  return {
    pickupMarker: isInbound ? getHospitalMarker() : getLocationPinMarker(),
    dropoffMarker: isInbound ? getLocationPinMarker() : getHospitalMarker(),
  };
};

/**
 * Calculate rotation angle between two GPS positions using Google geometry lib.
 */
export const calculateRotation = (previousLocation, currentLocation) => {
  if (!previousLocation || !currentLocation || !window.google?.maps?.geometry) {
    return 0;
  }

  try {
    const from = new window.google.maps.LatLng(
      previousLocation.latitude,
      previousLocation.longitude
    );
    const to = new window.google.maps.LatLng(
      currentLocation.latitude,
      currentLocation.longitude
    );

    return window.google.maps.geometry.spherical.computeHeading(from, to);
  } catch (error) {
    console.warn('Error calculating rotation:', error);
    return 0;
  }
};

/**
 * Smooth move helper — interpolates position for 60fps animation.
 * @param {{ lat: number, lng: number }} start
 * @param {{ lat: number, lng: number }} end
 * @param {(pos: { lat: number, lng: number }) => void} setPosition
 * @param {number} duration  ms
 * @returns {{ cancel: () => void }}
 */
export const smoothMove = (start, end, setPosition, duration = 1000) => {
  let animId = null;
  const startTime = performance.now();

  const animate = (now) => {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);

    const lat = start.lat + (end.lat - start.lat) * t;
    const lng = start.lng + (end.lng - start.lng) * t;
    setPosition({ lat, lng });

    if (t < 1) {
      animId = requestAnimationFrame(animate);
    }
  };

  animId = requestAnimationFrame(animate);

  return {
    cancel: () => {
      if (animId) cancelAnimationFrame(animId);
    },
  };
};

/**
 * Teardrop pin SVG generator (Google Maps style)
 * @param {'blue' | 'red'} type - 'blue' for pickup, 'red' for dropoff
 * @param {string} prefix - Optional prefix for SVG filter ID to avoid DOM collisions
 * @returns {string} Data URL for Google Maps Marker icon
 */
export const makePinSvg = (type = 'blue', prefix = 'pin') => {
  const isBlue = type === 'blue';
  const mainColor = isBlue ? '#1A73E8' : '#EA4335';
  const strokeColor = isBlue ? '#1557B0' : '#B31412';
  const dotColor = isBlue ? '#0D47A1' : '#7A0000';
  const filterId = `shadow_${prefix}_${type}`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
  <defs>
    <filter id="${filterId}" x="-20%" y="-10%" width="140%" height="130%">
      <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" flood-color="#000000" flood-opacity="0.25"/>
    </filter>
  </defs>
  <ellipse cx="14" cy="38" rx="5" ry="1.6" fill="rgba(0,0,0,0.2)"/>
  <path d="M14 1C7.37 1 2 6.37 2 13c0 8.8 10.4 23.5 11.4 24.8a0.8 0.8 0 0 0 1.2 0C15.6 36.5 26 21.8 26 13 26 6.37 20.63 1 14 1z"
        fill="${mainColor}"
        stroke="${strokeColor}"
        stroke-width="1"
        filter="url(#${filterId})"/>
  <circle cx="14" cy="13" r="4.5" fill="${dotColor}"/>
</svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Patient marker - teal teardrop pin with a white person badge, shown where the patient shared their own
 * location via the SMS / WhatsApp link. The colour and person badge keep it distinct from the blue pickup pin
 * (what the command centre entered) and the red drop-off pin. The tip of the pin is the exact location.
 */
export const makePatientPinSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
  <defs>
    <filter id="patient_pin_shadow" x="-30%" y="-20%" width="160%" height="150%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.3"/>
    </filter>
    <linearGradient id="patient_pin_fill" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#14A38B"/>
      <stop offset="1" stop-color="#0B7A66"/>
    </linearGradient>
  </defs>
  <ellipse cx="20" cy="49.5" rx="6" ry="1.8" fill="rgba(0,0,0,0.22)"/>
  <path d="M20 2C10.6 2 3 9.4 3 18.6c0 11.9 14.4 28 16 29.7a1.4 1.4 0 0 0 2 0c1.6-1.7 16-17.8 16-29.7C37 9.4 29.4 2 20 2z"
        fill="url(#patient_pin_fill)" stroke="#07594A" stroke-width="1" filter="url(#patient_pin_shadow)"/>
  <circle cx="20" cy="18.5" r="11.5" fill="#FFFFFF"/>
  <circle cx="20" cy="15" r="4.3" fill="#0B7A66"/>
  <path d="M12.6 25.2c0-4.2 3.3-6.6 7.4-6.6s7.4 2.4 7.4 6.6a11.5 11.5 0 0 1-14.8 0z" fill="#0B7A66"/>
</svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
